// hooks/useMessageAction.js

import { useEffect } from "react";

import {
  deleteMessageFromDB,
  markMessageDeletedForEveryone,
  markMessageDeletedForMe,
  loadMessages,
  saveMessage,
} from "../db/fsDB";

import { loadGroupKey } from "../utils/Cryptoutils";
import { fetchAndCachePublicKey } from "../utils/pbKey";
import { encryptDualDm, encryptGroup } from "../utils/encrypt";
import { getTombstoneText } from "../utils/getTombstoneText";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */

/** Resolves a message's canonical ID regardless of shape. */
const resolveMsgId = (m) =>
  m?.id ?? m?.messageId ?? m?.raw?.key?.msgId;

/** Resolves the sender's chatId regardless of message shape. */
const resolveSenderChatId = (m) =>
  m?.senderChatId ?? m?.raw?.key?.senderChatId ?? m?.sender;

/**
 * Fire-and-forget wrapper that silences expected rejections
 * (e.g. IDB write failures).
 */
const fireAndForget = (promise) =>
  Promise.resolve(promise).catch(() => {});

/* ─────────────────────────────────────────────
   HOOK
───────────────────────────────────────────── */
export function useMessageAction({
  socket,
  myChatId,
  selectedItem,
  setUsers,
  setGroups,
  setMessages,
  getUserKey,
  getGroupKey,
  groupMembers, // needed to determine admin role for group tombstone text
}) {
  /** Returns the in-memory cache key for the currently selected chat. */
  const getChatKey = () =>
    selectedItem?.type === "group"
      ? getGroupKey(selectedItem.groupId)
      : getUserKey(selectedItem.chatId);

  /* ============================================================
     🔥 DELETE (DM + GROUP)
  ============================================================ */
  const deleteMessages = ({ messageIds = [], forEveryone = false }) => {
    if (!socket || !selectedItem || !messageIds.length) return;

    const chatKey = getChatKey();

    let computedTombstone = null; // reuse for sidebar

    // ── Optimistic UI update ──────────────────────────────────
    setMessages((prev) => {
      const list = prev[chatKey] ?? [];

      let updated;

      if (forEveryone) {
        updated = list.map((m) => {
          const id = resolveMsgId(m);
          if (!messageIds.includes(id)) return m;

          if (m.isDeleted) return m;

          const senderChatId = resolveSenderChatId(m);

          const deletedByRole =
            selectedItem.type === "group" &&
            String(senderChatId) !== String(myChatId)
              ? "admin"
              : "user";

          const tombstoneText = getTombstoneText({
            deletedBy: myChatId,
            deletedByRole,
            myChatId,
            senderChatId,
          });

          if (!computedTombstone) {
            computedTombstone = tombstoneText;
          }

          return {
            ...m,
            isDeleted: true,
            deletedBy: myChatId,
            deletedByRole,
            text: tombstoneText,
          };
        });
      } else {
        // 🫥 DELETE FOR ME → remove immediately
        updated = list.filter((m) => {
          const id = resolveMsgId(m);
          return !messageIds.includes(id);
        });
      }

      return { ...prev, [chatKey]: updated };
    });

    // ── Sidebar update after "delete for me" ────────────────────
    if (!forEveryone) {
      setMessages((prev) => {
        const remaining = (prev[chatKey] ?? []);
        const newest = remaining.length > 0 ? remaining[remaining.length - 1] : null;
        const sidebarPayload = newest
          ? newest.isDeleted
            ? { text: newest.text, isDeleted: true }
            : newest.scheduledAt
            ? { text: newest.text || "📎 Media", isDeleted: false, isScheduled: true, scheduledAt: newest.scheduledAt }
            : newest.text || (newest.media ? "📎 Media" : "")
          : "";

        if (selectedItem.type === "user") {
          setUsers((prev) =>
            prev.map((u) =>
              String(u.chatId) === String(selectedItem.chatId)
                ? { ...u, lastMessage: sidebarPayload }
                : u
            )
          );
        } else {
          setGroups((prev) =>
            prev.map((g) =>
              String(g.groupId) === String(selectedItem.groupId)
                ? { ...g, lastMessage: sidebarPayload }
                : g
            )
          );
        }
        return prev; // no change to messages, just reading
      });
    }

    // ── Sidebar update (only for global delete) ─────────────────
    if (forEveryone && messageIds.length === 1) {
      const sidebarPayload = { text: computedTombstone, isDeleted: true };

      setUsers((prev) =>
        prev.map((u) => {
          if (
            selectedItem.type === "user" &&
            String(u.chatId) === String(selectedItem.chatId)
          ) {
            return { ...u, lastMessage: sidebarPayload, lastMessageTime: Date.now() };
          }
          return u;
        })
      );

      setGroups((prev) =>
        prev.map((g) => {
          if (
            selectedItem.type === "group" &&
            String(g.groupId) === String(selectedItem.groupId)
          ) {
            return { ...g, lastMessage: sidebarPayload, lastMessageTime: Date.now() };
          }
          return g;
        })
      );
    }

    // ── Persist to IDB (fire-and-forget) ───────────────────────
    const persistAll = messageIds.map((id) =>
      forEveryone
        ? fireAndForget(markMessageDeletedForEveryone(id))
        : fireAndForget(markMessageDeletedForMe(id, myChatId))
    );

    void Promise.all(persistAll);

    // ── Socket emit ───────────────────────────────────────────
    messageIds.forEach((msgId) => {
      if (selectedItem.type === "group") {
        socket.emit(
          forEveryone ? "group-delete-message" : "group-delete-for-me",
          { groupId: selectedItem.groupId, msgId }
        );
      } else {
        socket.emit(
          forEveryone ? "dm-delete-message" : "dm-delete-for-me",
          { peerId: selectedItem.chatId, msgId }
        );
      }
    });
  };

  /* ============================================================
     🔥 CLEAR CHAT (DM + GROUP)
  ============================================================ */
  const clearChat = async () => {
    if (!socket || !selectedItem) return;

    const chatKey = getChatKey();

    // ── Optimistic UI update ──────────────────────────────────
    setMessages((prev) => ({ ...prev, [chatKey]: [] }));

    // ── Sidebar reset ─────────────────────────────────────────
    setUsers((prev) =>
      prev.map((u) => {
        if (
          selectedItem.type === "user" &&
          String(u.chatId) === String(selectedItem.chatId)
        ) {
          return { ...u, lastMessage: "", lastMessageTime: null };
        }
        return u;
      })
    );

    setGroups((prev) =>
      prev.map((g) => {
        if (
          selectedItem.type === "group" &&
          String(g.groupId) === String(selectedItem.groupId)
        ) {
          return { ...g, lastMessage: "", lastMessageTime: null };
        }
        return g;
      })
    );

    // ── Purge IDB ─────────────────────────────────────────────
    try {
      const msgs = await loadMessages(chatKey);
      await Promise.allSettled(msgs.map((m) => deleteMessageFromDB(m.id)));
    } catch {
      // IDB load failed
    }

    // ── Socket emit ───────────────────────────────────────────
    if (selectedItem.type === "group") {
      socket.emit("group-clear-chat", { groupId: selectedItem.groupId });
    } else {
      socket.emit("dm-clear-chat", { peerId: selectedItem.chatId });
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handler = ({ peerId }) => {
      const chatKey = getUserKey(peerId);

      setMessages((prev) => ({ ...prev, [chatKey]: [] }));

      setUsers((prev) =>
        prev.map((u) =>
          String(u.chatId) === String(peerId)
            ? { ...u, lastMessage: "", lastMessageTime: null }
            : u
        )
      );
    };

    socket.on("dm-clear-chat", handler);
    return () => socket.off("dm-clear-chat", handler);
  }, [socket, myChatId]);

  useEffect(() => {
    if (!socket) return;

    const handler = ({ groupId }) => {
      const chatKey = getGroupKey(groupId);

      setMessages((prev) => ({ ...prev, [chatKey]: [] }));

      setGroups((prev) =>
        prev.map((g) =>
          String(g.groupId) === String(groupId)
            ? { ...g, lastMessage: "", lastMessageTime: null }
            : g
        )
      );
    };

    socket.on("group-clear-chat", handler);
    return () => socket.off("group-clear-chat", handler);
  }, [socket, myChatId]);

  /* ============================================================
     ✏️ EDIT MESSAGE (DM + GROUP)
  ============================================================ */
  const editMessage = async ({ msgId, newText }) => {
    if (!socket || !selectedItem || !msgId || !newText?.trim()) return;

    const chatKey = getChatKey();
    const trimmed = newText.trim();

    // ── Optimistic text update ────────────────────────────────
    setMessages((prev) => {
      const list = prev[chatKey] ?? [];
      return {
        ...prev,
        [chatKey]: list.map((m) => {
          if (resolveMsgId(m) !== msgId) return m;
          return { ...m, text: trimmed, isEdited: true };
        }),
      };
    });

    /* ── GROUP EDIT ────────────────────────────────────────── */
    if (selectedItem.type === "group") {
      const groupId = selectedItem.groupId;

      try {
        const groupKey = await loadGroupKey(groupId);
        if (!groupKey) return;

        const enc = await encryptGroup(trimmed, groupKey);
        if (!enc) return;

        socket.emit("group-edit-message", {
          groupId,
          msgId,
          newCiphertext: enc.ciphertext,
          newIv: enc.iv,
        });

        void (async () => {
          try {
            const msgs = await loadMessages(chatKey);
            const oldMsg = msgs.find((m) => m.id === msgId);
            if (oldMsg) {
              await saveMessage({
                ...oldMsg,
                encryptedText: {
                  ...oldMsg.encryptedText,
                  ciphertext: enc.ciphertext,
                  iv: enc.iv,
                },
                isEdited: true,
              });
            }
          } catch {
            // IDB group edit save failed
          }
        })();
      } catch {
        // group edit failed
      }

      return;
    }

    /* ── DM EDIT ───────────────────────────────────────────── */
    if (selectedItem.type === "user") {
      const peerId = selectedItem.chatId;

      try {
        const [recipientPublicKey, senderPublicKey] = await Promise.all([
          fetchAndCachePublicKey(peerId, socket),
          fetchAndCachePublicKey(myChatId, socket),
        ]);

        if (!recipientPublicKey || !senderPublicKey) return;

        const result = await encryptDualDm(trimmed, recipientPublicKey, senderPublicKey);
        if (!result) return;

        const { forRecipient, forSender } = result;

        socket.emit("dm-edit-message", {
          peerId,
          msgId,
          newCiphertext:               forRecipient.ciphertext,
          newIv:                       forRecipient.iv,
          newEphemeralPublicKey:        forRecipient.ephemeralPublicKey,
          newCiphertextSender:         forSender.ciphertext,
          newIvSender:                 forSender.iv,
          newEphemeralPublicKeySender: forSender.ephemeralPublicKey,
        });

        void (async () => {
          try {
            const msgs = await loadMessages(chatKey);
            const oldMsg = msgs.find((m) => m.id === msgId);
            if (oldMsg) {
              await saveMessage({
                ...oldMsg,
                encryptedText: {
                  ...oldMsg.encryptedText,
                  ciphertext:         forSender.ciphertext,
                  iv:                 forSender.iv,
                  ephemeralPublicKey: forSender.ephemeralPublicKey,
                },
                isEdited: true,
              });
            }
          } catch {
            // IDB dm edit save failed
          }
        })();
      } catch {
        // dm edit failed
      }
    }
  };

  /* ============================================================
     📌 PIN MESSAGE (DM + GROUP)
  ============================================================ */
  const pinMessage = ({ msgId, duration = "7d" }) => {
    if (!socket || !selectedItem || !msgId) return;

    const chatKey = getChatKey();

    setMessages((prev) => {
      const list = prev[chatKey] ?? [];

      return {
        ...prev,
        [chatKey]: list.map((m) => {
          if (resolveMsgId(m) !== msgId) return m;

          let updatedMsg;

          if (selectedItem.type === "group") {
            updatedMsg = { ...m, pinnedBy: myChatId, pinnedAt: Date.now() };
          } else {
            const current = Array.isArray(m.pinnedBy) ? m.pinnedBy : [];
            updatedMsg = {
              ...m,
              pinnedBy: [...new Set([...current, myChatId])],
              pinnedAt: Date.now(),
            };
          }

          fireAndForget(saveMessage(updatedMsg));
          return updatedMsg;
        }),
      };
    });

    if (selectedItem.type === "group") {
      socket.emit("group-pin-message", {
        groupId: selectedItem.groupId,
        msgId,
        duration,
      });
    } else {
      socket.emit("dm-pin-message", {
        peerId: selectedItem.chatId,
        msgId,
        duration,
      });
    }
  };

  /* ============================================================
     📌 UNPIN MESSAGE (DM + GROUP)
  ============================================================ */
  const unpinMessage = ({ msgId }) => {
    if (!socket || !selectedItem || !msgId) return;

    const chatKey = getChatKey();

    setMessages((prev) => {
      const list = prev[chatKey] ?? [];

      return {
        ...prev,
        [chatKey]: list.map((m) => {
          if (resolveMsgId(m) !== msgId) return m;

          let updatedMsg;

          if (selectedItem.type === "group") {
            updatedMsg = { ...m, pinnedBy: null, pinnedAt: null, expiresAt: null };
          } else {
            const current = Array.isArray(m.pinnedBy) ? m.pinnedBy : [];
            const updated = current.filter((id) => id !== myChatId);
            updatedMsg = {
              ...m,
              pinnedBy: updated,
              ...(updated.length === 0 ? { pinnedAt: null, expiresAt: null } : {}),
            };
          }

          fireAndForget(saveMessage(updatedMsg));
          return updatedMsg;
        }),
      };
    });

    if (selectedItem.type === "group") {
      socket.emit("group-unpin-message", {
        groupId: selectedItem.groupId,
        msgId,
      });
    } else {
      socket.emit("dm-unpin-message", {
        peerId: selectedItem.chatId,
        msgId,
      });
    }
  };

  return {
    deleteMessages,
    clearChat,
    editMessage,
    pinMessage,
    unpinMessage,
  };
}